import { Column, Inline, Stack, Text, breakpoints, textVariants } from '@etg/wings';
import type {
  BrandedFareFeature,
  CabinBaggageTripSummary,
  CheckedBaggageSummary,
} from '@eti/schema-types';
import { useProperty, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { XCircle } from '@phosphor-icons/react';
import CabinBaggageNotIncludedFeature from './CabinBaggageNotIncludedFeature';
import PersonalItemAndCabinBaggageNotIncludedFeature from './PersonalItemAndCabinBaggageNotIncludedFeature';
import CheckedBaggageNotIncludedFeature from './CheckedBaggageNotIncludedFeature';
import isCheckedBaggageNotIncluded from './utils/isCheckedBaggageNotIncluded';
import isCabinBaggageNotIncluded from './utils/isCabinBaggageNotIncluded';
import { iconStyles } from './CabinBaggageFeature';

const featureListStyles = css`
  margin-top: 8px;
`;

const featureTextStyles = css`
  color: #737373;
  width: -webkit-fill-available; /* stylelint-disable */
`;

interface NotIncludedFeaturesProps {
  cabinBaggageTripSummary: CabinBaggageTripSummary;
  checkedBaggageSummary: CheckedBaggageSummary;
  notIncludedFeatures: BrandedFareFeature[];
  ticketingCarrierCodes: string[];
  isPersonalItemIncluded: boolean;
}
const NotIncludedFeatures = ({
  cabinBaggageTripSummary,
  checkedBaggageSummary,
  notIncludedFeatures,
  ticketingCarrierCodes = [],
  isPersonalItemIncluded,
}: NotIncludedFeaturesProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const shouldUseCabinBaggageTextUsingAtpco = p('BrandedFares.CabinBaggage.From.Atpco.Enabled');
  const shouldShowPersonalItem = p('BrandedFares.ShowPersonalBaggage.Enabled');
  const carriersNotIncludedProducts = p('BrandedFares.ShowNotIncluded.Carriers') || [];
  const showNotIncludedProducts = carriersNotIncludedProducts.some((item: string) =>
    ticketingCarrierCodes.includes(item),
  );
  if (
    !showNotIncludedProducts ||
    (notIncludedFeatures.length === 0 &&
      !isCheckedBaggageNotIncluded(checkedBaggageSummary) &&
      !isCabinBaggageNotIncluded(cabinBaggageTripSummary, showNotIncludedProducts))
  ) {
    return null;
  }
  return (
    <Column
      data-testid="not-included-features"
      width={{
        [breakpoints._0]: '4/4',
        [breakpoints._768]: '2/4',
      }}
    >
      <Text variant={textVariants._14_BOLD}>{t('PaymentPage.BrandedFare.NotIncluded.Title')}</Text>
      <Stack as="ul" className={featureListStyles} spacing={8}>
        {!shouldUseCabinBaggageTextUsingAtpco && shouldShowPersonalItem && (
          <PersonalItemAndCabinBaggageNotIncludedFeature
            cabinBaggageTripSummary={cabinBaggageTripSummary}
            isPersonalItemIncluded={isPersonalItemIncluded}
            showNotIncludedProducts={showNotIncludedProducts}
          />
        )}
        {!shouldUseCabinBaggageTextUsingAtpco && !shouldShowPersonalItem && (
          <CabinBaggageNotIncludedFeature
            cabinBaggageTripSummary={cabinBaggageTripSummary}
            showNotIncludedProducts={showNotIncludedProducts}
          />
        )}
        <CheckedBaggageNotIncludedFeature checkedBaggageSummary={checkedBaggageSummary} />
        {notIncludedFeatures.map((feature: BrandedFareFeature) => (
          <Inline as="li" key={feature.code} noWrap role="listitem" spacing={8}>
            <XCircle className={iconStyles} color="#737373" size={20} weight="thin" />
            <Text as="p" className={featureTextStyles}>
              {feature.name}
            </Text>
          </Inline>
        ))}
      </Stack>
    </Column>
  );
};
export default NotIncludedFeatures;
